import { LocalStorageHelpers } from "@ai360/core";

/**
 * The identity flow the local dev server is running. Derived from the same
 * VITE_* vars the npm scripts already set, so `start:sso` and `start:sso:core`
 * collapse to the same mode (LOCAL_CORE is node-side only and never reaches the
 * client).
 *
 *   "standard" — `npm run start`, default pool, `cognito:username`
 *   "sky"      — `npm run start:sky`, sky pool, `custom:skyid`. Its tokens carry
 *                `cognito:username` as well, so branch on `custom:skyid` first.
 *   "sso"      — `npm run start:sso`, the DEFAULT pool. VITE_SSO_REDIRECT only
 *                un-suppresses cross-subdomain SSO redirects; it does not change
 *                pools, so the claim matches `start`, not `start:sky`.
 */
export type DevAuthMode = "sky" | "sso" | "standard";

/** localStorage key recording which dev auth mode the cached session belongs to. */
const DEV_AUTH_MODE = "devAuthMode";

/** Amplify namespaces every user-pool token under this prefix + the client id. */
const AMPLIFY_TOKEN_PREFIX = "CognitoIdentityServiceProvider.";

/**
 * Keys written when arriving from SKY (auth/callback → ReceiveAuthentication). While
 * USER_INFO_KEY exists, fetchCognitoConfig re-points Amplify at the sky pool no matter
 * which script is running — the reason a leftover SSO session keeps handing out
 * `custom:skyid` under `npm run start`. LEGACY_DB likewise changes which company
 * fetchLoginUser auto-selects.
 *
 * IOT_CONFIG is deliberately NOT cleared: getIotConfig returns the endpoint/region/
 * identity pool, none of which branch on the user pool, and the identity-pool
 * credentials exchange is never cached.
 */
const SKY_HANDOFF_KEYS = [
    "LEGACY_DB",
    "USER_INFO_KEY",
    "cognito-access-token",
    "cognito-id-token",
    "cognito-refresh-token",
];

export const getDevAuthMode = (env: ImportMetaEnv = import.meta.env): DevAuthMode => {
    if (env.VITE_USE_SKY_IDP === "true") {
        return "sky";
    }
    if (env.VITE_SSO_REDIRECT === "true") {
        return "sso";
    }
    return "standard";
};

/**
 * Remove everything that would let a session from the previous mode be restored:
 * the cached pool config, every Amplify token (they are namespaced per client id,
 * so both pools' tokens can coexist), the SKY handoff keys, and the last-used
 * org pointers request.ts sends as headers. Deliberately the same set a manual
 * "clear site data" wipes, minus REMEMBER_ME/USERNAME so the login form still
 * pre-fills — matching logoutUser in ~/login/sagas.
 */
const clearCachedSession = (): void => {
    LocalStorageHelpers.remove(LocalStorageHelpers.COGNITO_CONFIG);
    LocalStorageHelpers.remove(LocalStorageHelpers.COMPANY_GUID);
    LocalStorageHelpers.remove(LocalStorageHelpers.USER_GUID);
    SKY_HANDOFF_KEYS.forEach((key) => LocalStorageHelpers.remove(key));

    const amplifyKeys: string[] = [];
    for (let index = 0; index < window.localStorage.length; index++) {
        const key = window.localStorage.key(index);
        if (key != null && key.startsWith(AMPLIFY_TOKEN_PREFIX)) {
            amplifyKeys.push(key);
        }
    }
    amplifyKeys.forEach((key) => LocalStorageHelpers.remove(key));
};

/**
 * Clears the cached Cognito session when the dev server is started in a different
 * auth mode than the one that produced it, so switching between `npm run start`,
 * `start:sso`, and `start:sky` no longer requires clearing browser storage by hand.
 *
 * Without this, each direction fails in its own way: leftover SKY handoff keys keep
 * `npm run start` pinned to the sky pool, and a live default-pool session lets
 * checkSessionIsActive restore it before `start:sso` ever reaches an SSO redirect.
 *
 * This is a local-debugging convenience ONLY: it requires the Vite dev server
 * (import.meta.env.DEV), and production builds bake DEV to false, so no env var or
 * runtime state can make a production build discard a user's session — do not weaken
 * this gate to a runtime flag. Vitest (MODE "test") is excluded so the suite never
 * mutates localStorage at import time.
 *
 * Returns the active mode, or null when the reset does not apply.
 */
export const resetCachedSessionOnDevAuthModeChange = (
    env: ImportMetaEnv = import.meta.env
): DevAuthMode | null => {
    if (!env.DEV || env.MODE === "test") {
        return null;
    }
    const mode = getDevAuthMode(env);
    const previousMode = LocalStorageHelpers.get(DEV_AUTH_MODE);
    if (previousMode !== mode) {
        // A missing marker also purges: storage from before this marker existed is
        // exactly the mixed-up state this exists to fix, so it cannot be trusted.
        console.warn(
            `Dev auth mode is "${mode}" (was ${
                previousMode == null ? "unknown" : `"${previousMode}"`
            }); clearing the cached Cognito session so this mode logs in fresh.`
        );
        clearCachedSession();
        LocalStorageHelpers.set(DEV_AUTH_MODE, mode);
    }
    return mode;
};

// Runs at import time, from the first import in index.jsx, so the cache is already
// consistent before any module reads auth state at module scope.
resetCachedSessionOnDevAuthModeChange();
